import {
  addToast,
  Button,
  Modal,
  ModalBody,
  ModalContent,
  ModalFooter,
  ModalHeader,
  useDisclosure,
} from '@heroui/react'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import config from 'config'
import { Plus } from 'lucide-react'
import { useState } from 'react'
import { $fetch } from 'utils/fetch'
import FileUpload from './FileUpload'
import { guidelines } from './utils'

export default function AddVoiceSampleModal({ voiceId }) {
  const { isOpen, onOpen, onOpenChange } = useDisclosure()
  const [file, setFile] = useState()
  const queryClient = useQueryClient()

  const { mutate: createVoiceSample, isPending: isCreatingVoiceSample } = useMutation({
    mutationFn: async () => {
      const formData = new FormData()
      formData.append('audio_samples', file)
      formData.append('voice_id', voiceId)
      return $fetch('/v1/voices/samples', {
        method: 'POST',
        body: formData,
        baseURL: config.FILE_UPLOAD_URL,
      })
    },
    onSuccess: () => {
      setFile(null)
      queryClient.invalidateQueries({ queryKey: ['samples', voiceId] })
      onOpenChange(false)
    },
    onError: (error) => {
      addToast({
        color: 'danger',
        title: 'Error adding sample',
        description: error.data?.errors?.[0]?.message || 'Ensure the file type and size are valid.',
      })
    },
  })

  return (
    <>
      <Button
        disableRipple
        onPress={onOpen}
        size="sm"
        radius="sm"
        variant="bordered"
        color="default"
        fullWidth
        className="h-7"
      >
        <Plus size={16} />
        Add Sample
      </Button>

      <Modal
        radius="sm"
        size="3xl"
        isOpen={isOpen}
        onOpenChange={onOpenChange}
        classNames={{
          footer: 'ml-auto border-t border-gray-200 w-full',
          body: 'flex flex-row gap-5 py-5',
          header: 'border-b border-gray-200 bg-[#18181b] w-full',
        }}
      >
        <ModalContent>
          {onClose => (
            <>
              <ModalHeader className="flex flex-col gap-1">
                <h2 className={`
                  flex items-center gap-3 font-taurus text-2xl font-semibold
                  text-white
                `}
                >
                  Add a new sample to your voice
                </h2>
                <p className={`
                  font-taurus text-xs font-light tracking-wider
                  text-foreground-400
                `}
                >
                  Adding a sample will help our AI learn your voice's unique characteristics.
                </p>
              </ModalHeader>
              <ModalBody>
                <FileUpload
                  label="Add a sample"
                  fileOrFiles={file}
                  setFileOrFiles={setFile}
                  multiple={false}
                  types={['wav']}
                  className="my-auto"
                />
                <div className="flex flex-col gap-3">
                  {guidelines.slice(1).map((guideline, index) => {
                    const Icon = guideline.icon
                    return (
                      <div key={index} className="flex items-start gap-3">
                        <Icon size={16} className="mt-1 shrink-0 text-gray-800" />
                        <p className="text-xs text-default-500">{guideline.text}</p>
                      </div>
                    )
                  })}
                </div>
              </ModalBody>
              <ModalFooter>
                <Button color="danger" radius="sm" variant="light" onPress={onClose}>
                  Close
                </Button>
                <Button
                  color="primary"
                  radius="sm"
                  type="submit"
                  isLoading={isCreatingVoiceSample}
                  isDisabled={!file}
                  onPress={createVoiceSample}
                >
                  Add Sample
                </Button>
              </ModalFooter>
            </>
          )}
        </ModalContent>
      </Modal>
    </>
  )
}
